<script setup lang="ts" generic="T">
/**
 * DataTable — generic typed table with TanStack Table backing.
 *
 * Sprint 2 will use this for every CRUD screen. The contract is
 * server-side paginated: the consumer fetches a page (typically via
 * Vue Query) and passes `data`, `total`, `page`, `size`. The table
 * renders rows for the current page only and emits `update:page` /
 * `update:sort` events for the consumer to refetch.
 *
 * Why TanStack Table: it gives us column-visibility, sorting model,
 * row-selection, and column-pinning primitives without us writing
 * the state machine. We only style the markup.
 *
 * Loading and empty states are handled inline so consumers don't
 * juggle them.
 *
 * Usage:
 *   <DataTable
 *     :data="users.data?.content ?? []"
 *     :columns="columns"
 *     :total="users.data?.total ?? 0"
 *     :page="page"
 *     :size="20"
 *     :loading="users.isFetching"
 *     @update:page="page = $event"
 *   />
 *
 * Columns are defined as TanStack ColumnDef<T> with our Streama
 * conventions in the cell renderers (StatusDot, Avatar, etc).
 */
import { computed } from 'vue'
import {
  FlexRender,
  type ColumnDef,
  getCoreRowModel,
  useVueTable,
} from '@tanstack/vue-table'
import Spinner from './Spinner.vue'
import EmptyState from './EmptyState.vue'
import Pagination from './Pagination.vue'

interface Props {
  data: T[]
  columns: ColumnDef<T, unknown>[]
  total: number
  page: number
  size: number
  loading?: boolean
  /** Title shown in the empty-state when no data is present. */
  emptyTitle?: string
  /** Description for the empty state. */
  emptyDescription?: string
  /** Hide pagination controls (for screens with <1 page). */
  hidePagination?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  loading: false,
  hidePagination: false,
})

defineEmits<{
  'update:page': [page: number]
  'row-click': [row: T]
}>()

const table = useVueTable({
  get data() {
    return props.data
  },
  get columns() {
    return props.columns
  },
  getCoreRowModel: getCoreRowModel(),
  // We do server-side pagination/sorting — TanStack just renders
  // what we feed it.
  manualPagination: true,
  manualSorting: true,
})

const isEmpty = computed(
  () => !props.loading && props.data.length === 0,
)
</script>

<template>
  <div class="overflow-hidden">
    <!-- Loading overlay -->
    <div
      v-if="loading"
      class="flex items-center justify-center py-12"
    >
      <Spinner size="lg" />
    </div>

    <!-- Empty state -->
    <EmptyState
      v-else-if="isEmpty"
      :title="emptyTitle ?? $t('table.empty.title')"
      :description="emptyDescription ?? $t('table.empty.description')"
    />

    <!-- Table -->
    <div v-else class="overflow-x-auto">
      <table class="w-full text-xs">
        <thead>
          <tr class="border-b border-border bg-surface-2/50">
            <th
              v-for="header in table.getHeaderGroups()[0]?.headers ?? []"
              :key="header.id"
              :style="
                header.column.columnDef.size
                  ? { width: `${header.column.columnDef.size}px` }
                  : undefined
              "
              class="px-3 py-2 text-left text-[10px] font-bold uppercase
                     tracking-wider text-text-4"
            >
              <FlexRender
                v-if="!header.isPlaceholder"
                :render="header.column.columnDef.header"
                :props="header.getContext()"
              />
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in table.getRowModel().rows"
            :key="row.id"
            :data-testid="(row.original as Record<string, unknown>).id ? `partner-row-${(row.original as Record<string, unknown>).id}` : undefined"
            class="border-b border-border hover:bg-surface-2/40
                   transition-colors cursor-pointer"
            @click="$emit('row-click', row.original)"
          >
            <td
              v-for="cell in row.getVisibleCells()"
              :key="cell.id"
              class="px-3 py-2.5 text-text-2"
            >
              <FlexRender
                :render="cell.column.columnDef.cell"
                :props="cell.getContext()"
              />
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <Pagination
      v-if="!hidePagination && !loading && !isEmpty"
      :page="page"
      :size="size"
      :total="total"
      @update:page="(p: number) => $emit('update:page', p)"
    />
  </div>
</template>
